<script lang="ts" setup>
import { ContactService, toastServiceError } from '@/services';
import { useContactTimelineFilterStore, useFilterStore, useUiStore } from '@/stores';
import { CallModel, ContactModel, ContactTimelineStats, DataItem, Permission } from '@/types';
import { applyPhoneMask } from '@/utils/number';
import { BookOpenIcon, MailIcon, PhoneIcon } from '@heroicons/vue/solid';
import { computed, onMounted, ref, Ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import {
    ContactAuraPanel,
    ContactHealthScoreChart,
    ContactTimelineCallDetails,
    ContactTimelineFilters,
} from '@/views/pages/contact/components';
import moment from 'moment';
import { ContactTimelineModel } from '@/types/models/ContactTimelineModel';
import { formatSecToTime } from '@/utils/datetime';
import { useI18n } from 'vue-i18n';
import { ContactHealthScoreStats } from '@/types/metrics/ContactHealthScore';
import { timelineAnalysisMock } from './mocks/TimelineAnalysis';

const showAskAuraButton = false;

const route = useRoute();
const uiStore = useUiStore();
const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);
const { t } = useI18n();

const contact: Ref<ContactModel | undefined> = ref(undefined);
const timelineStats: Ref<ContactTimelineStats | undefined> = ref(undefined);
const calls: Ref<CallModel[]> = ref([]);
const count = ref(0);
const perPage = 20;
const currentPage = ref(1);

const filterStore = useFilterStore();
const timelineStore = useContactTimelineFilterStore();

const dateRange = computed<Array<Date>>({
    get: () => filterStore.dateRange,
    set: (range) => filterStore.setDateRange(range),
});

const mainEmail = computed(() => contact.value?.emails.find((email) => email.isMain) ?? null);

const mainPhone = computed(() => contact.value?.phones.find((phone) => phone.isMain) ?? null);

const fetchContact = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.get<ContactModel>(id);
        contact.value = response.data;
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const fetchContactCalls = async (id: number) => {
    await setLoading(true);
    try {
        const response = await ContactService.getTimeline<ContactTimelineModel>(id, {
            params: {
                startDate: moment.utc(dateRange.value[0]).format('YYYY-MM-DD'),
                endDate: moment.utc(dateRange.value[1]).format('YYYY-MM-DD'),
                callType: timelineStore.callType?.value ?? null,
                page: currentPage.value,
                per_page: perPage,
            },
        });
        calls.value = response.data.calls;
        timelineStats.value = response.data.stats;
        count.value = Number(response.headers['x-pagination-count']);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const daysSinceLastContact = computed(() => {
    if (!timelineStats.value?.lastContactAt) return null;
    const lastContactDate = moment(timelineStats.value.lastContactAt);
    const today = moment();
    return today.diff(lastContactDate, 'days');
});

const healthScore: ContactHealthScoreStats = {
    score: 59,
    analysis:
        'A relação com a Fintrust Capital vinha estável sob tratativas de integração e renovação contratual, mas sofreu uma queda recente de satisfação que reduziu o health score para 59. O principal ponto de atrito foi uma indisponibilidade de 40 minutos no sistema em horário de pico, o que gerou forte insatisfação e a exigência de reembolso e de um relatório de causa raiz. Como próxima ação recomendada, deve-se formalizar o envio do relatório técnico de causa raiz e conceder o crédito na fatura solicitado para restabelecer a confiança do cliente antes da renovação de fim de ano.',
};

const headerStats = computed<DataItem<string>[]>(() => {
    if (!timelineStats.value) return [];

    return [
        {
            name: 'totalCalls',
            value: timelineStats.value.totalCalls.toString(),
        },
        {
            name: 'averageDuration',
            value: formatSecToTime(timelineStats.value.averageDuration, true),
        },
        {
            name: 'positivePercentage',
            value:
                timelineStats.value.positivePercentage !== null
                    ? `${timelineStats.value.positivePercentage}%`
                    : '-',
        },
        {
            name: 'daysSinceLastContact',
            value: t('contact.timeline.stats.daysCount', { n: daysSinceLastContact.value ?? 0 }),
        },
    ];
});

const factors = timelineAnalysisMock.factors;

watch(
    [() => filterStore.dateRange, () => timelineStore.callType],
    () => {
        const contactId = Number(route.params.id);
        if (currentPage.value === 1) fetchContactCalls(contactId);
        else currentPage.value = 1;
    },
    { deep: true },
);

watch(currentPage, () => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) fetchContactCalls(contactId);
});

onMounted(() => {
    const contactId = Number(route.params.id);
    if (!isNaN(contactId)) {
        fetchContact(contactId);
        fetchContactCalls(contactId);
    }
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="flex items-center text-xl font-semibold text-gray-800">
                    <BookOpenIcon class="mr-1 h-6 w-6 text-gray-500" aria-hidden="true" />
                    {{ $t('contact.timeline.section.contact.title') }}
                </h1>
            </template>
            <template #actions>
                <div class="flex space-x-4">
                    <UiBackButton :fallbackTo="{ name: 'ListContact' }" />
                    <ContactAuraPanel v-if="showAskAuraButton" />
                    <router-link
                        v-if="$can(Permission.EDIT_CONTACT)"
                        :to="{
                            name: 'EditContactById',
                            params: {
                                id: route.params.id,
                            },
                        }"
                        class="block hover:bg-gray-50"
                    >
                        <UiButton variant="primary" text-variant="white">
                            {{ $t('core.actions.Edit') }}
                        </UiButton>
                    </router-link>
                </div>
            </template>
        </UiPageHeader>

        <div class="space-y-4">
            <UiPanel>
                <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    <div>
                        <h3 class="mb-1 font-semibold">
                            {{ $t('contact.timeline.section.contact.name') }}
                        </h3>
                        <p>{{ contact?.name }}</p>
                    </div>
                </div>
                <div class="mt-2 flex flex-wrap justify-between">
                    <div class="flex items-center space-x-1 text-xs text-gray-600">
                        <span>{{ $t('contact.timeline.section.contact.company') }}: </span>
                        <template v-if="contact?.contactCompany">
                            <div class="ml-1">
                                {{ contact?.contactCompany }}
                            </div>
                        </template>
                        <span v-else>-</span>
                    </div>
                    <div class="flex items-center space-x-1 text-xs text-gray-600">
                        <span>{{ $t('contact.timeline.section.contact.ownerUser') }}: </span>
                        <div class="ml-1">
                            {{ contact?.ownerUser.name }}
                        </div>
                    </div>
                </div>
            </UiPanel>
            <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <UiPanel>
                    <h3 class="mb-4 text-base font-semibold text-gray-800">
                        {{ $t('contact.timeline.section.email') }}
                    </h3>
                    <div v-if="contact?.emails.length === 0">
                        <p>{{ $t('contact.errors.noEmails') }}</p>
                    </div>
                    <UiPanel v-else class="flex items-center bg-white">
                        <MailIcon
                            class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                            aria-hidden="true"
                        />
                        {{ mainEmail?.email }}
                    </UiPanel>
                </UiPanel>
                <UiPanel>
                    <h3 class="mb-4 text-base font-semibold text-gray-800">
                        {{ $t('contact.timeline.section.phone') }}
                    </h3>
                    <UiPanel v-if="mainPhone" class="flex items-center bg-white">
                        <PhoneIcon
                            class="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400"
                            aria-hidden="true"
                        />
                        {{ applyPhoneMask(mainPhone?.number) }}
                    </UiPanel>
                </UiPanel>
            </div>
        </div>

        <!-- Contact Analysis -->
        <UiPanel>
            <div class="flex flex-col gap-6 md:flex-row md:items-start">
                <ContactHealthScoreChart :healthScore="healthScore" />
                <div class="flex-1">
                    <h2
                        class="text-muted-foreground text-xs font-semibold uppercase tracking-wider"
                    >
                        {{ $t('contact.timeline.stats.analysis.title') }}
                    </h2>
                    <p class="mt-2 text-sm leading-relaxed">
                        {{ healthScore?.analysis }}
                    </p>
                </div>
            </div>
            <dl
                v-if="headerStats.length"
                class="mt-6 grid grid-cols-2 gap-4"
                :class="['sm:grid-cols-' + headerStats.length]"
            >
                <UiPanel v-for="stat in headerStats" :key="stat.name">
                    <dt class="text-center text-xs font-medium text-gray-500">
                        {{ $t('contact.timeline.stats.' + stat.name) }}
                    </dt>
                    <dd class="mt-1 text-center text-2xl font-semibold text-gray-800">
                        {{ stat.value }}
                    </dd>
                </UiPanel>
            </dl>
            <div class="mt-6 rounded-lg border bg-gray-300/40 p-4">
                <h3 class="text-xs font-bold uppercase text-gray-600">
                    {{ $t('contact.timeline.stats.analysis.healthScoreCalculation.title') }}
                </h3>
                <p class="mt-2 text-sm leading-relaxed text-gray-600">
                    {{ $t('contact.timeline.stats.analysis.healthScoreCalculation.description') }}
                </p>
                <ul class="mt-4 grid gap-3 md:grid-cols-3">
                    <li
                        v-for="factor in factors"
                        :key="factor.key"
                        class="bg-card rounded-lg border border-gray-300 bg-white p-4"
                    >
                        <p class="text-xs uppercase text-gray-500">
                            {{
                                $t(
                                    'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                        factor.key +
                                        '.title',
                                )
                            }}
                        </p>
                        <p class="mt-1 text-base font-semibold">
                            {{ factor.value }}
                        </p>
                        <p class="mt-1 text-xs text-gray-600">
                            <template v-if="factor.key === 'predominantSentiment'">
                                {{
                                    $t(
                                        'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                            factor.key +
                                            '.description',
                                        {
                                            count: factor.count,
                                            percentage: factor.percentage,
                                        },
                                    )
                                }}
                            </template>
                            <template v-else-if="factor.key === 'estimatedSatisfaction'">
                                {{
                                    $t(
                                        'contact.timeline.stats.analysis.healthScoreCalculation.factors.' +
                                            factor.key +
                                            '.description',
                                        {
                                            count: factor.count,
                                        },
                                    )
                                }}
                            </template>
                            <template v-else>
                                {{
                                    $t(
                                        `contact.timeline.stats.analysis.healthScoreCalculation.factors.${factor.key}.${factor.trend}`,
                                    )
                                }}
                            </template>
                            <template class="mt-3 flex flex-wrap gap-1.5">
                                <UiButton
                                    v-for="evidence in factor.evidence"
                                    :key="evidence.id"
                                    class="text-primary rounded border px-2 py-1 text-[11px] font-medium transition-colors hover:bg-gray-100"
                                >
                                    {{ evidence.label }}
                                    {{ evidence.satisfaction }}
                                </UiButton>
                            </template>
                        </p>
                    </li>
                </ul>
            </div>
        </UiPanel>

        <!-- Timeline -->
        <div class="space-y-4 pt-4">
            <div class="flex flex-wrap items-center justify-between gap-4">
                <h2 class="text-lg font-semibold text-gray-800">
                    {{ $t('contact.timeline.title') }}
                </h2>
                <ContactTimelineFilters />
            </div>
            <ul role="list" class="my-2 space-y-2">
                <ContactTimelineCallDetails v-for="call in calls" :key="call.id" :call="call" />
                <li
                    v-if="!calls.length"
                    class="mt-0.5 flex h-48 flex-col flex-wrap items-center justify-center rounded-b bg-gray-200 font-semibold text-gray-600"
                >
                    {{ $t('core.messages.noRecordsFound') }}
                </li>
            </ul>
            <UiPagination
                v-if="calls.length"
                v-model="currentPage"
                :count="count"
                :perPage="perPage"
            />
        </div>
    </div>
</template>
